import { Checkbox } from '@zillow/constellation';
import {
  StyledColumnText,
  StyledHeaderOverlayWrapper,
  StyledIconChevronDown,
  StyledIconContainer,
  StyledSelectAllContainer,
  StyledTableColumn,
  StyledTableHeaderCell,
  StyledTableHeaderRow,
} from './StyledComponents';
import type { ColumnConfig } from './types';
import type { AdvancedTableStyles, SortKeyType } from './AdvancedTable';
import { formatColWidth } from './utils';

interface TableMFHHeaderProps<T> {
  columnConfig: Array<ColumnConfig<T>>;
  columnDivisions: number;
  canSelectRow: boolean;
  disableSelectAll: boolean;
  edgePadding: number;
  hasExpandableRows: boolean;
  isMobile: boolean;
  isSelected: boolean;
  onSelectToggle: () => void;
  onSortClick?: ({ key, customKey }: { key?: keyof T; customKey?: string }) => void;
  selectionDisabled: boolean;
  sortKey?: SortKeyType<T>;
  tableStyle: AdvancedTableStyles;
  minWeightWidth: number;
  useFixedWidth: boolean;
  fixedWidth: number;
}

const ADDITIONAL_PADDING = 16;

const TableMFHHeader = <T,>({
  canSelectRow = true,
  columnConfig = [],
  disableSelectAll = false,
  sortKey = undefined,
  isSelected = false,
  onSelectToggle = () => {},
  onSortClick,
  columnDivisions,
  selectionDisabled,
  hasExpandableRows,
  edgePadding,
  tableStyle,
  minWeightWidth,
  useFixedWidth,
  fixedWidth,
}: TableMFHHeaderProps<T>) => {
  const formatValue = (col: ColumnConfig<T>) => (col.headerRenderer ? col.headerRenderer() : col.header);
  const formatOverlayWidth = (width: number) => `${(width / columnDivisions) * fixedWidth - ADDITIONAL_PADDING}px`;

  let rowSelector: JSX.Element;
  if (canSelectRow && !disableSelectAll) {
    rowSelector = (
      <StyledSelectAllContainer
        display="flex"
        justifyContent="center"
        style={{ width: formatColWidth(1, columnDivisions) }}
      >
        <StyledIconContainer display="flex" justifyContent="center">
          <Checkbox checked={isSelected} onChange={onSelectToggle} disabled={selectionDisabled} />
        </StyledIconContainer>
      </StyledSelectAllContainer>
    );
  } else if (canSelectRow && disableSelectAll) {
    rowSelector = <div style={{ width: formatColWidth(1, columnDivisions) }} />;
  }

  const getSortDirection = (column: ColumnConfig<T>) => {
    const finalizedKey = sortKey?.key || sortKey?.customKey;

    if (!finalizedKey) {
      return;
    }

    if (finalizedKey === column.dataKey || finalizedKey === column.customKey) {
      if (sortKey.ascending) {
        return 'ascending';
      } else {
        return 'descending';
      }
    }
  };

  const renderOverlay = () => {
    const column = columnConfig[0];
    return (
      <StyledHeaderOverlayWrapper tableStyle={tableStyle} width={formatOverlayWidth(column.width)}>
        <StyledTableColumn align={column.align}>
          <StyledTableHeaderCell
            align={column.align}
            role="button"
            tabIndex={0}
            isSortable={column.isSortable}
            onClick={() =>
              column.isSortable && !!onSortClick && onSortClick({ customKey: column.customKey, key: column.dataKey })
            }
          >
            <StyledColumnText fontType="bodySmallHeading">{formatValue(column)}</StyledColumnText>
            {column.isSortable && <StyledIconChevronDown size="xs" sortDirection={getSortDirection(column)} />}
          </StyledTableHeaderCell>
        </StyledTableColumn>
      </StyledHeaderOverlayWrapper>
    );
  };

  const renderTableHeaderInnerContent = () => {
    return (
      <>
        {rowSelector}
        {columnConfig.map((column, i) => {
          return (
            <StyledTableColumn
              key={`col-header-${i}`}
              align={column.align}
              width={formatColWidth(column.width, columnDivisions)}
            >
              <StyledTableHeaderCell
                align={column.align}
                role="button"
                tabIndex={0}
                isSortable={column.isSortable}
                onClick={() =>
                  column.isSortable &&
                  !!onSortClick &&
                  onSortClick({ customKey: column.customKey, key: column.dataKey })
                }
              >
                {formatValue(column)}
                {column.isSortable && <StyledIconChevronDown size="xs" sortDirection={getSortDirection(column)} />}
              </StyledTableHeaderCell>
            </StyledTableColumn>
          );
        })}
        {hasExpandableRows && <StyledTableColumn width={formatColWidth(1, columnDivisions)} />}
      </>
    );
  };

  return (
    <StyledTableHeaderRow
      display="flex"
      width={useFixedWidth ? minWeightWidth : undefined}
      tableStyle={tableStyle}
      style={{
        paddingLeft: edgePadding,
        paddingRight: edgePadding,
        backgroundColor: 'white',
      }}
      data-test="header"
    >
      {useFixedWidth && columnConfig.length > 0 && renderOverlay()}
      {renderTableHeaderInnerContent()}
    </StyledTableHeaderRow>
  );
};

export default TableMFHHeader;
